import { track } from '@vercel/analytics';

import type { VaEventName } from '@/lib/analytics/event-catalog';

/** Flat payload allowed by Vercel custom events (no nested objects). */
export type VaEventData = Record<string, string | number | boolean | null>;

const MAX_LEN = 255;

export function sanitizeVaData(data?: Record<string, unknown>): VaEventData | undefined {
  if (!data) return undefined;

  const out: VaEventData = {};
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) continue;

    const safeKey = key.trim().slice(0, MAX_LEN);
    if (!safeKey) continue;

    if (value === null) {
      out[safeKey] = null;
      continue;
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
      out[safeKey] = value;
      continue;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim().slice(0, MAX_LEN);
      if (trimmed.length > 0) out[safeKey] = trimmed;
      continue;
    }
  }

  return Object.keys(out).length > 0 ? out : undefined;
}

/** Client-side Vercel custom event. No-op on server. */
export function vaTrack(event: VaEventName | string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  const safeName = event.trim().slice(0, MAX_LEN);
  if (!safeName) return;

  const payload = sanitizeVaData(data);
  if (payload) {
    track(safeName, payload);
  } else {
    track(safeName);
  }
}
